import css from "../../../css/modules/toolbar.module.scss";
import { usePageApi } from "./PageApi";
import { useDepartments } from "../../../hooks/api/departments";
import { useCallback } from "react";
import { Button, FormControl, Slider, Tooltip } from "@mui/material";
import { translate } from "../../../utils/translate";
import { MultiselectDropdown } from "../../../components/Toolbar/MultiselectDropdown";
import { useOrderCategories } from "../../../hooks/api/orderCategories";
import { useProjectCategories } from "../../../hooks/api/projectCategories";
import { getDepartmentFilter } from "../ganttConfig";
import { Toolbar } from "./ToolBar";
import clsx from "clsx";
import SortIcon from "@mui/icons-material/Sort";
import { ToggleButton } from '@mui/material';
import { getWorkingTime } from "../../../utils/ganttUtils.jsx";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

const getValueText = (value) => `${value}%`;

function ValueLabelComponent(props) {
  const { children, open, value } = props;

  return (
    <Tooltip open={open} enterTouchDelay={0} placement="bottom" title={value}>
      {children}
    </Tooltip>
  );
}

export function InitialToolbar({ overview }) {
  const {
    api,
    update,
    showTreeStructure,
    showToolbar,
    projectCategoriesSelected,
    orderCategoriesSelected,
    departmentsSelected,
    minOfferScoringPercentage,
  } = usePageApi();
  const { data: departments = [] } = useDepartments();
  const { data: orderCategories = [] } = useOrderCategories();
  const { data: projectCategories = [] } = useProjectCategories();

  const { t } = useTranslation();

  useEffect(() => {
    document.title = t("Project total overview") + " | Project Assistant";
  }, []);

  const onDepartmentChange = useCallback(
    (departments) => {
      const departmentsFilter = departments.reduce((res, i) => {
        res[i] = true;
        return res;
      }, {});
      update({
        departmentsSelected: departments,
      });
      overview.applyDepartmentsFilter(departments).then(() => {
        const project = api.histogram?.project;
        if (project) {
          project.resourceStore.filter(getDepartmentFilter(departmentsFilter));
        }
      });
    },
    [update, overview, api]
  );
  const onProjectCategoryChange = useCallback(
    (projectCategoriesSelected) => {
      update({
        projectCategoriesSelected,
      });
    },
    [update]
  );
  const onOrderCategoryChange = useCallback(
    (orderCategoriesSelected) => {
      update({
        orderCategoriesSelected,
      });
    },
    [update]
  );
  const onMinOfferScoringPercentageChange = useCallback(
    (event, checked) => {
      update({
        minOfferScoringPercentage: checked,
      });
    },
    [update]
  );
  
  const onApply = useCallback(async () => {
    
    const oldState = {
      hideWeekends: null
    };

    
    let oldIndicators = {
      constraintDate: !!overview?.props?.api?.gantt?.features?.indicators?.items?.constraintDate,
      deadlineDate: !!overview?.props?.api?.gantt?.features?.indicators?.items?.deadlineDate,
      earlyDates: !!overview?.props?.api?.gantt?.features?.indicators?.items?.earlyDates,
      lateDates: !!overview?.props?.api?.gantt?.features?.indicators?.items?.lateDates,
      viewPresetId: overview?.props?.api?.gantt?.viewPreset?.id
    }
    let columnFilters = overview?.props?.api?.gantt?.taskStore?.filters?._values;

    if (api.gantt?.isVisible) {
      oldState.hideWeekends = (api.gantt?.workingTime?.fromDay === 1);
    }
    
    overview.setState(
      {
        project: null,
        loading: true,
      },
      
      () => {
        pendo.track("Total project overview | Load data", {
            ProjectCategories: projectCategoriesSelected || undefined,
            OrderCategories: orderCategoriesSelected || undefined,
            MinOfferScoringPercentage: minOfferScoringPercentage || undefined,
            ShowTreeStructure: showTreeStructure,
          });
        overview.configureGantt({
          params: {
            ProjectCategories: projectCategoriesSelected || undefined,
            OrderCategories: orderCategoriesSelected || undefined,
            MinOfferScoringPercentage: minOfferScoringPercentage || undefined,
            ShowTreeStructure: showTreeStructure,
            oldIndicators: oldIndicators,
            columnFilters : columnFilters
          },
          departments: departmentsSelected,
        });

        if (overview.ganttConfig && oldState.hideWeekends !== null) {
          overview.ganttConfig.workingTime = getWorkingTime(oldState.hideWeekends);
        }
        
      }
    );
    
  }, [
    overview,
    projectCategoriesSelected,
    orderCategoriesSelected,
    minOfferScoringPercentage,
    departmentsSelected,
    showTreeStructure
  ]);
  
  const onChangeTreeStructure = useCallback(() => {
    update({
      showTreeStructure: !showTreeStructure,
    });
  }, [showTreeStructure, update]);

  return (
    <div className={css.initialToolbar}>
      <FormControl className={css.formControl} data-testid="tb_projectoverview_project_category">
        <MultiselectDropdown
          items={projectCategories}
          onItemsChange={onProjectCategoryChange}
          descriptionKey={"Description"}
          placeholder={translate("Project category")}
          itemKey={"PK_Category"}
          placeholderSelected={"project categories selected"}
        />
      </FormControl>
      <FormControl className={css.formControl} data-testid="tb_projectoverview_order_category">
        <MultiselectDropdown
          items={orderCategories}
          onItemsChange={onOrderCategoryChange}
          descriptionKey={"Description"}
          placeholder={translate("Order category")}
          itemKey={"PK_Category"}
          placeholderSelected={"order categories selected"}
        />
      </FormControl>

      <FormControl className={clsx(css.formControl, css.sliderControl)} data-testid="tb_projectoverview_project_probability_slider">
        <label htmlFor="MinOfferScoringPercentage" className={css.minOfferLabel}>
          {translate("Probability of sale")} {">"}=
        </label>
        <Slider
          id="MinOfferScoringPercentage"
          onChange={onMinOfferScoringPercentageChange}
          defaultValue={100}
          min={0}
          max={100}
          valueLabelDisplay="on"
          slots={{
            valueLabel: ValueLabelComponent,
          }}
          getAriaValueText={getValueText}
          aria-label="Probability of sale >="
        />
      </FormControl>
      <div className={css.btnContainer}>
        <FormControl className={css.formControl}>
          <Tooltip title={translate("Toggle tree structure")}>
            <ToggleButton
              value="showTreeStructure"
              onChange={onChangeTreeStructure}
              selected={showTreeStructure}
              size="small"
              className={css.iconBtn}
            >
              <SortIcon style={{ transform: "rotateY(0.5turn)" }} />
            </ToggleButton>
          </Tooltip>
        </FormControl>
      </div>
      <Button variant="contained" color="primary" onClick={onApply} data-testid="tb_projectoverview_apply">
        <span className="ico icon-equalizer" />
        &nbsp;&nbsp; {translate("Apply")}
      </Button>
      
      <FormControl className={css.formControl} data-testid="tb_projectoverview_department">
        <MultiselectDropdown
          items={departments}
          onItemsChange={onDepartmentChange}
          descriptionKey={"Description"}
          placeholder={translate("Department")}
          itemKey={"PK_Department"}
          placeholderSelected={"departments selected"}
        />
      </FormControl>
      {showToolbar && <Toolbar overview={overview} />}
    </div>
  );
}
